import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, useToast } from '@vd/ui';
import { AuthLayout } from './AuthLayout';
import { useAuth } from '../../features/auth/AuthProvider';
import { api } from '../../lib/api';
import { WorkspaceMark } from '../../features/workspaces/WorkspaceMark';

export function AcceptInvitePage() {
  const { token } = useParams();
  const { user, refreshUser, setCurrentCompany } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Public lookup so we can render the inviting company + role before
  // the user authenticates. Server-side rate-limited to slow guessing.
  const { data: invite, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => (token ? api.lookupInvite(token) : Promise.reject(new Error('no token'))),
    enabled: !!token,
    retry: false,
  });

  if (!token) {
    return (
      <AuthLayout title="Invite link invalid" footer={<Link to="/signin">Sign in</Link>}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>This invite link is missing or has expired.</p>
      </AuthLayout>
    );
  }

  if (isLoading) {
    return (
      <AuthLayout title="Loading invite…">
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Checking your invitation…</p>
      </AuthLayout>
    );
  }

  if (error || !invite) {
    return (
      <AuthLayout title="Invite unavailable" footer={<Link to="/signin">Sign in</Link>}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          {(error as Error)?.message ?? 'This invite link is invalid or has expired.'}
        </p>
      </AuthLayout>
    );
  }

  const onAccept = async () => {
    if (!user) return; // shouldn't reach here for an unauthenticated user — see below
    setSubmitting(true);
    try {
      const result = await api.acceptInvite(token);
      toast.push({
        variant: 'success',
        title: `Joined ${result.company.name}`,
        description: `Role: ${result.company.role}`,
      });
      await refreshUser();
      setCurrentCompany(result.company.id);
      navigate('/', { replace: true });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Accept failed', description: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  // Three branches:
  //   1. User signed in with the invited email → one-tap Accept
  //   2. User signed in with a DIFFERENT email → must sign out first
  //   3. Not signed in → either sign in (existing account) or sign up
  //      (new account); the signup endpoint auto-accepts using the token.
  const signedInWrongEmail =
    user && user.email.toLowerCase() !== invite.email.toLowerCase();

  const inviteSummary = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <WorkspaceMark company={{ id: 'invite', name: invite.companyName, color: invite.companyColor ?? undefined }} size={32} />
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5, margin: 0 }}>
        <strong>{invite.inviterName}</strong> invited <strong>{invite.email}</strong> to join{' '}
        <strong>{invite.companyName}</strong> as a <strong>{invite.role}</strong>.
      </p>
    </div>
  );

  if (signedInWrongEmail) {
    return (
      <AuthLayout title="Wrong account" footer={<Link to="/signin">Sign in as someone else</Link>}>
        {inviteSummary}
        <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
          You're signed in as {user!.email}. This invite is for {invite.email}.
          Sign out first, then click the invite link again.
        </p>
      </AuthLayout>
    );
  }

  if (user) {
    return (
      <AuthLayout title="Accept invitation" footer={<Link to="/">Cancel</Link>}>
        {inviteSummary}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button variant="primary" size="lg" loading={submitting} disabled={submitting} onClick={onAccept}>
            Accept invite
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Not signed in.
  const signupHref = `/signup?email=${encodeURIComponent(invite.email)}&invite=${encodeURIComponent(token)}`;
  const signinHref = `/signin?next=${encodeURIComponent('/invite/' + token)}&email=${encodeURIComponent(invite.email)}`;
  // Secondary button — for the "I have a separate identity on a
  // different email" case. Drops the email pre-fill and the invite
  // token so the user can sign in to whatever account they want;
  // they're effectively bailing on this invite for now.
  const differentEmailSigninHref = '/signin';

  return (
    <AuthLayout
      title={`Join ${invite.companyName}`}
      footer={<Link to="/">Cancel</Link>}
    >
      {inviteSummary}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        {invite.hasAccount ? (
          <Button variant="primary" size="lg" onClick={() => navigate(signinHref)}>
            Sign in &amp; join
          </Button>
        ) : (
          <>
            <Button variant="primary" size="lg" onClick={() => navigate(signupHref)}>
              Create account &amp; join
            </Button>
            <Button variant="secondary" size="md" onClick={() => navigate(differentEmailSigninHref)}>
              Sign in with a different email
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
